import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiIcon, EuiLink, EuiPanel, EuiSpacer, EuiText, EuiTitle, EuiBadge } from '@elastic/eui'

import { PostalCodeTarget } from 'api/interfaces'
import { MasterCampaignPlanStatus } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, CampaignTransferStatus } from 'api/interfaces/MasterCampaignStub'
import { useTransferCampaignToDspMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { fetchCampaign } from 'features/campaigns/campaign/campaignSlice'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'

import BackgroundContentCard from '../pages/MasterCampaignPlanPage/components/BackgroundContentCard'

interface CampaignCardProps {
  builder: MasterCampaignPlanCampaignBuilder
  onEditClick?: () => void
  onRemoveCampaignClick?: () => Promise<void>
  onRemoveCampaignLoading: boolean
  masterCampaignPlanId: string
  masterCampaignPlanStatus?: MasterCampaignPlanStatus
  showActions?: boolean
}

export const CampaignCard = ({ builder, onEditClick, onRemoveCampaignLoading, masterCampaignPlanId, masterCampaignPlanStatus, showActions = true, ...props }: CampaignCardProps) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false)
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<{ city: string; postalCodes: PostalCodeTarget[] }[]>([])
  const [isExpanded, setIsExpanded] = useState(true)

  const [transferCampaignToDsp, transferCampaignToDspResult] = useTransferCampaignToDspMutation()

  useEffect(() => {
    const grouped: { city: string; postalCodes: PostalCodeTarget[] }[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!grouped.find(g => g.city === city)) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup?.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  useEffect(() => {
    if (!transferCampaignToDspResult.isSuccess || !transferCampaignToDspResult.data) {
      return
    }

    setIsTransferModalVisible(false)
  }, [transferCampaignToDspResult, history])

  const onRemoveCampaignClick = () => {
    setIsRemoveModalVisible(true)
  }

  const onConfirmRemoveCampaignClick = async () => {
    await props.onRemoveCampaignClick?.()
    setIsRemoveModalVisible(false)
  }

  const onTransferClick = () => {
    setIsTransferModalVisible(true)
  }

  const onConfirmTransferClick = () => {
    if (!currentOrganization?.id || !currentAccount?.id || !masterCampaignPlanId || !builder.id) {
      return
    }

    transferCampaignToDsp({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      masterCampaignPlanId: masterCampaignPlanId,
      campaignBuilderId: builder.id
    })
  }

  const onViewInDspClick = (dspCampaignId?: string) => {
    if (!currentAccount?.id || !masterCampaignPlanId || !dspCampaignId) {
      return
    }

    dispatch(fetchCampaign(currentAccount?.id ?? '', dspCampaignId))
    history.push(`/campaigns/edit/${dspCampaignId}/details`)
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const shouldShowTransferButton = !builder.isOffAdCritter && masterCampaignPlanStatus === MasterCampaignPlanStatus.Ready && (!builder.transferStatus || builder.transferStatus === CampaignTransferStatus.NotTransferred)

  const shouldShowViewInDspButton = builder.transferStatus === CampaignTransferStatus.Transferred && builder.dspCampaignId

  const campaignTypeDisplay = builder.isOffAdCritter && builder.offAdCritterName ? `${builder.offAdCritterName}` : builder.campaignType

  return (
    <>
      <div id={`campaign-${builder.id}`}>
        <EuiFlexGroup>
          <EuiFlexItem>
            <EuiPanel hasShadow={true} style={{ backgroundColor: '#ede7dd', padding: '3rem 5rem' }}>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <EuiTitle size={'s'}>
                    <p>{builder.name}</p>
                  </EuiTitle>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  {showActions && masterCampaignPlanStatus !== MasterCampaignPlanStatus.TransferredToDsp && (!builder.transferStatus || builder.transferStatus === CampaignTransferStatus.NotTransferred) && (
                    <>
                      <EuiFlexGroup justifyContent='flexEnd'>
                        <EuiFlexItem grow={false}>
                          <EuiButtonIcon iconType={'pencil'} style={{ color: '#0071c2' }} onClick={onEditClick} />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                          <EuiButtonIcon iconType={'trash'} style={{ color: '#BD271E' }} onClick={onRemoveCampaignClick} />
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </>
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size='m' />
              <EuiFlexGroup>
                <EuiFlexItem>
                  <span>
                    <b>Campaign Type:</b> {campaignTypeDisplay}
                    <br />
                    <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                    <br />
                    <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)})` : ''}
                  </span>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  {shouldShowTransferButton && (
                    <EuiButton size='s' color='primary' fill onClick={onTransferClick}>
                      Transfer to DSP
                    </EuiButton>
                  )}
                  {shouldShowViewInDspButton && (
                    <EuiButton size='s' color='primary' onClick={() => onViewInDspClick(builder.dspCampaignId)}>
                      View in DSP
                    </EuiButton>
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <span>
                    <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                    {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                    {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                  </span>
                </EuiFlexItem>
              </EuiFlexGroup>

              <EuiSpacer size='l' />

              {builder.longSummary && (
                <EuiFlexGroup>
                  <EuiFlexItem>
                    <b>Summary</b>
                    <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                    <EuiSpacer size='s' />
                    <EuiText size='s'>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{builder.longSummary}</p>
                    </EuiText>
                    <EuiSpacer size='l' />
                  </EuiFlexItem>
                </EuiFlexGroup>
              )}

              {isExpanded && (
                <div>
                  <EuiSpacer size='l' />

                  {builder.seasonalitySummary && (
                    <EuiFlexGroup>
                      <EuiFlexItem>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <b>Timing</b>
                          <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                          <EuiText size='s'>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{builder.seasonalitySummary}</p>
                          </EuiText>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  )}

                  <EuiFlexGroup alignItems='stretch'>
                    {(builder.geoSummary || groupedPostalCodes.length > 0) && (
                      <EuiFlexItem grow={4}>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <>
                            <div>
                              <b>Geography</b>
                              <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                              {builder.geoSummary && (
                                <>
                                  <EuiSpacer size='s' />
                                  <EuiText size='s'>
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{builder.geoSummary}</p>
                                  </EuiText>
                                </>
                              )}
                            </div>
                            <EuiSpacer size={'m'} />
                            <ul style={{ marginLeft: '1rem' }}>
                              {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                                <li key={index}>
                                  <b>{city}</b>
                                  <EuiSpacer size='xs' />
                                  {postalCodes.map((postalCode, index) => (
                                    <ul key={index}>
                                      <li key={index}>{postalCode.code}</li>
                                    </ul>
                                  ))}
                                  <EuiSpacer size='s' />
                                </li>
                              ))}
                            </ul>
                          </>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    )}
                    {(builder.audienceSummary || (builder.segmentTargets?.length ?? 0) > 0) && (
                      <EuiFlexItem grow={6}>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <div>
                            <b>Audience Segments</b>
                            <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                            {builder.audienceSummary && (
                              <>
                                <EuiSpacer size='s' />
                                <EuiText size='s'>
                                  <p style={{ whiteSpace: 'pre-wrap' }}>{builder.audienceSummary}</p>
                                </EuiText>
                              </>
                            )}
                          </div>
                          <EuiSpacer size={'m'} />
                          <ul style={{ marginLeft: '1rem' }}>
                            {builder.segmentTargets.map((segment, index) => (
                              <li key={index}>
                                <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b> (Segment {segment.id})
                                <div>
                                  <EuiSpacer size='xs' />
                                  {segment.name}
                                  <EuiSpacer size='m' />
                                </div>
                              </li>
                            ))}
                          </ul>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </div>
              )}
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>

      {isRemoveModalVisible && (
        <AgencyV2ModalOld isLoading={onRemoveCampaignLoading} title='Remove Campaign' onClose={() => setIsRemoveModalVisible(false)} confirmButtonText='Remove' onConfirmButtonClick={onConfirmRemoveCampaignClick} showCancelButton>
          <EuiText>
            Campaign: <strong>{builder.name}</strong> will be removed from the plan.
          </EuiText>
        </AgencyV2ModalOld>
      )}

      {isTransferModalVisible && (!builder.transferStatus || builder.transferStatus === CampaignTransferStatus.NotTransferred) && (
        <AgencyV2ModalOld isLoading={transferCampaignToDspResult.isLoading} title='Transfer Campaign to DSP' onClose={() => setIsTransferModalVisible(false)} confirmButtonText='Transfer' onConfirmButtonClick={onConfirmTransferClick} showCancelButton bodyHeight='10em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>
                This will transfer the campaign <strong>{builder.name}</strong> to AdCritter DSP where you will have full editing control.
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Are you ready to transfer this campaign to the DSP?</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2ModalOld>
      )}
    </>
  )
}
